import React, { startTransition, useCallback, useEffect, useMemo } from 'react';

import { isEmpty } from 'lodash';
import { connect } from 'react-redux';

import {
  getLastWatchedEpgs,
  getLiveChannels,
  setCurrentVideo,
  setVideoOptions,
  Translation
} from 'common/actions';
import { setDetailedInfoAsset } from 'common/actions/detailed-info';
import { Card, LiveCard } from 'common/components/cards';
import { PaidChannelPopup } from 'common/components/message-popups';
import { Stripe } from 'common/components/stripe';
import { ViewWithDetails } from 'common/components/view-with-details';
import { ViewWithDetailsRefObject } from 'common/components/view-with-details/ViewWithDetails';
import variables from 'common/config/variables';
import {
  Epg,
  IViewWithDetailsStripeItem,
  LiveCategories,
  LiveChannel,
  LiveChannels
} from 'common/constants/data-types';
import { usePopup } from 'common/contexts/popup';
import protect from 'common/HoC/protect';
import { IDetailedInfo } from 'common/reducers/detailedInfo';
import { FavoriteChannels } from 'common/reducers/favoriteChannels';
import { IVideoPlayer } from 'common/reducers/videoPlayer';
import { getChannelLogo } from 'common/services/helpers';
import { groupByCategory } from 'common/utils';
import { useParentalLock, usePlayerActions } from 'common/utils/hooks';
import { usePinPopup } from 'common/utils/hooks/usePinPopup';
import { PromiseDispatch } from 'index';

interface StateProps {
  i18n: Translation;
  liveCategories: LiveCategories;
  liveChannels: LiveChannels;
  favoriteChannels: FavoriteChannels;
  lastWatched: Epg[];
  logos: any;
  detailedInfo: IDetailedInfo;
  videoPlayer: IVideoPlayer;
}

interface HomeProps extends StateProps {
  getLiveChannels: PromiseDispatch;
  getLastWatchedEpgs: PromiseDispatch;
  setCurrentVideo: any;
  setVideoOptions: any;
  setDetailedInfoAsset: (asset: IDetailedInfo['asset']) => void;
  currentPlayingChannelId?: string;
}

function Home(props: HomeProps) {
  const { openPinPopup } = usePinPopup();
  const { openPopup } = usePopup();
  const { locked: channelsLocked, unlock, getChannelsLockedState } = useParentalLock();
  const viewWithDetailsRef = React.useRef<ViewWithDetailsRefObject>(null);
  const { playVideoFromContinueWatching: playVideo } = usePlayerActions();

  useEffect(() => {
    const id = requestIdleCallback(
      async () => {
        await props.getLiveChannels();
        await props.getLastWatchedEpgs();
      },
      { timeout: variables.THROTTLE_TIMEOUT }
    );
    return () => {
      cancelIdleCallback(id);
    };
  }, []);

  // Group live channels
  const liveRows = useMemo(() => {
    const { liveChannels, liveCategories } = props;

    if (isEmpty(props.liveChannels)) return [];

    if (!isEmpty(liveChannels) && !isEmpty(liveCategories)) {
      const formated = Object.values(liveChannels)
        .filter((liveChannel) => liveChannel.visible)
        .map((channel: LiveChannel) => ({
          ...channel,
          isLiveCard: true,
          major_category: liveCategories[channel.category]?.name || props.i18n.common.other
        }));
      return groupByCategory(formated);
    }
    return [];
  }, [props.liveChannels, props.liveCategories, props.i18n?.common?.other, channelsLocked]);

  const favoriteChannels = useMemo(
    () =>
      Object.values(props.favoriteChannels)
        .filter((e) => e.visible)
        .map((e) => ({
          ...e,
          isLiveCard: true
        })),
    [props.favoriteChannels]
  );

  const lastWatched = useMemo(
    () => props.lastWatched?.filter((e) => e.visible),
    [props.lastWatched]
  );

  const openPlayerFromContinueWatching = useCallback((channel: IViewWithDetailsStripeItem) => {
    openPlayer(channel, true);
  }, []);

  const openPlayer = useCallback(
    (channel: IViewWithDetailsStripeItem, continueWatching?: boolean) => {
      const video = channel.isLiveCard ? channel.currentepg : channel;
      const channelsLockedState = getChannelsLockedState();
      const playingSameChannel = props.currentPlayingChannelId === video.chan_id;

      const handlePlay = () => {
        playVideo(video, {
          continueWatching,
          channelPosition: channel.isLiveCard ? channel.position : null
        });
        props.setVideoOptions({
          isFullscreen: true
        });
      };

      if (!channel.access) {
        openPopup({
          id: 'paid-channel-popup',
          type: 'primary',
          content: <PaidChannelPopup popupId="paid-channel-popup" />
        });
      } else if (channel.locked && channelsLockedState && !playingSameChannel) {
        openPinPopup({
          onSuccess: () => {
            unlock();
            handlePlay();
          }
        });
      } else {
        handlePlay();
      }
    },
    [props.currentPlayingChannelId]
  );

  const renderLiveCard = useCallback(
    (item: LiveChannel, index: number) => {
      return (
        <LiveCard
          key={`live-card-${item.id}`}
          liveChannel={item}
          logo={props.logos[item.id]?.png}
          i18n={props.i18n}
          channelsLocked={channelsLocked}
          testId={`live-card-${index}`}
          enableContinueWatching
        />
      );
    },
    [props.logos, props.i18n, channelsLocked]
  );

  const renderFavoriteCard = useCallback(
    (item: LiveChannel, index: number) => {
      return (
        <LiveCard
          key={`live-card-${item.id}`}
          liveChannel={item}
          logo={props.logos[item.id]?.png}
          i18n={props.i18n}
          channelsLocked={channelsLocked}
          testId={`favorite-live-card-${index}`}
          enableContinueWatching
        />
      );
    },
    [props.logos, props.i18n, channelsLocked]
  );

  const renderArchiveCard = useCallback(
    (item: Epg, index: number) => (
      <Card
        key={`archive-card-${item.eventid}`}
        epg={item}
        logo={getChannelLogo(item.chan_id, props.logos)}
        enableContinueWatching
        testId={`archive-card-${index}`}
        i18n={props.i18n}
      />
    ),
    [props.logos, props.i18n]
  );
  const renderFavoriteList = useCallback(() => {
    if (isEmpty(favoriteChannels)) {
      return null;
    }
    return (
      <Stripe
        key={'favorite-channels'}
        items={favoriteChannels}
        renderItem={renderFavoriteCard}
        title={props.i18n.channelList.favorites}
        onStripeItemPress={openPlayerFromContinueWatching}
        testId="favorite-channels-stripe"
      />
    );
  }, [favoriteChannels, renderFavoriteCard]);

  const renderLiveChannelList = useCallback(() => {
    return liveRows.map((row, index) => {
      return (
        <Stripe
          key={`live-channels-stripe-${index}`}
          testId={`live-channels-stripe-${row.title}`}
          title={row.title}
          onStripeItemPress={openPlayerFromContinueWatching}
          items={row.items}
          renderItem={renderLiveCard}
        />
      );
    });
  }, [liveRows, renderLiveCard]);

  const renderLastWatched = useCallback(() => {
    if (isEmpty(lastWatched)) {
      return null;
    }
    return (
      <Stripe
        key={'last-watched'}
        items={lastWatched}
        renderItem={renderArchiveCard}
        title={props.i18n?.channelList?.lastWatched}
        onStripeItemPress={openPlayerFromContinueWatching}
        testId="last-watched-stripe"
      />
    );
  }, [lastWatched]);

  useEffect(() => {
    const currentAsset = viewWithDetailsRef.current?.currentAsset;
    if (currentAsset && currentAsset.isLiveCard && !isEmpty(props.liveChannels)) {
      const current = props.liveChannels[currentAsset.epg.chan_id];

      startTransition(() =>
        viewWithDetailsRef.current?.setDetailedInfoAsset({
          epg: current?.currentepg,
          isLiveCard: currentAsset?.isLiveCard,
          resolution: current?.resolution
        })
      );
    }
  }, [props.liveChannels]);

  useEffect(() => {
    if (viewWithDetailsRef.current?.currentAsset && !isEmpty(props.lastWatched)) {
      const epg = props.lastWatched.find(
        (epg) => epg.eventid === viewWithDetailsRef.current?.currentAsset?.epg.eventid
      );
      epg &&
        startTransition(() =>
          viewWithDetailsRef.current?.setDetailedInfoAsset({
            ...viewWithDetailsRef.current?.currentAsset,
            epg
          })
        );
    }
  }, [props.lastWatched]);

  const getSelectedAssetData = useCallback(
    (asset: IViewWithDetailsStripeItem) => {
      if (asset && !isEmpty(props.liveChannels)) {
        const epg = asset.isLiveCard ? props.liveChannels[asset.id]?.currentepg : asset;

        // if adult channel return undefined
        if (props.liveChannels[asset.id]?.adult) {
          return undefined;
        }

        const newAsset = {
          epg,
          isLiveCard: !!asset.isLiveCard,
          resolution: asset.isLiveCard ? asset.resolution : props.liveChannels[asset.id]?.resolution
        };
        return epg && newAsset;
      }
      return undefined;
    },
    [props.liveChannels]
  );

  return (
    <ViewWithDetails
      ref={viewWithDetailsRef}
      focusKey={location.pathname}
      getSelectedAssetData={getSelectedAssetData}
    >
      {[renderLastWatched(), renderFavoriteList(), ...renderLiveChannelList()]}
    </ViewWithDetails>
  );
}

const mapStateToProps = ({
  liveChannels,
  logos,
  liveCategories,
  lastWatched,
  i18n,
  favoriteChannels,
  videoPlayer
}: StateProps) => ({
  i18n,
  liveChannels,
  favoriteChannels,
  logos,
  liveCategories,
  lastWatched,
  currentPlayingChannelId: videoPlayer.channelId
});

export default React.memo(
  protect(
    connect(mapStateToProps, {
      getLiveChannels,
      getLastWatchedEpgs,
      setCurrentVideo,
      setVideoOptions,
      setDetailedInfoAsset
    })(Home)
  )
);
